import { useState, useEffect, useCallback, useRef, useContext } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { useLocation } from 'react-router-dom'
import CreateJs from 'preload-js'
import { Context } from '@/context'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import Meta from '@/components/Meta'
import Hero from '@/components/Hero'
import CategoryDescription from '@/components/CategoryDescription'
import RevealGrid from '@/components/RevealGrid'
import Badge from '@/components/Badge'
import Footer from '@/components/Footer'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import actions from '@/actions'
import { getSlug } from '@/utils/path'
import style from './style'

const useStyles = createUseStyles(style)

const Assicurazioni = ({
  cpt: cptFromProps,
}) => {
  const { getImageUrl, setPageAnimationReady, headerHeight } = useContext(Context)
  const classes = useStyles({ headerHeight })
  const [load, setLoad] = useState(false)
  const [insurance, setInsurance] = useState([])
  const preload = useRef(new CreateJs.LoadQueue())
  const $root = useRef()
  const location = useLocation()
  const stub = useRef(getSlug(location.pathname))

  /*------------------------------
  Executes a callback on loading each image
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, listCpt, pages, mediaQueryKey } = useSelector((state) => ({
    current: state.page.current,
    page: state.page.pages[stub.current] || {},
    pages: state.page.pages,
    listCpt: state.page.archive[cptFromProps],
    mediaQueryKey: state.mediaQuery.key,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  const getCPT = useCallback((cpt, index) => dispatch(actions.page.getListCPT(cpt, index)), [dispatch])
  const updatePageReady = useCallback((index) => dispatch(actions.loading.setReady(index)), [dispatch])
  const setSiteLoaded = useCallback(() => dispatch(actions.loading.setSiteLoaded()), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    updatePageReady(true)
    setSiteLoaded()
    setLoad(true)
  }

  /*------------------------------
  Preload Main Images: preloading only thre three first image
  ------------------------------*/
  const preloadMedia = () => {
    preload.current.on('complete', completePreload)
    const heroMedia = getImageUrl(page.featured_media, 'full', stub.current, 'pages')
    if (page.featured_media !== 0) preload.current.loadFile(heroMedia)
  }

  /*------------------------------
  Initialize (API Calls)
  ------------------------------*/
  const initialize = () => {
    if (current !== stub.current) setCurrentPage(stub.current, 'pages')
    if (Object.keys(page).length === 0) getPage(stub.current)
    if (!listCpt && cptFromProps) getCPT(cptFromProps)
  }

  /*------------------------------
  Set Insurance
  ------------------------------*/
  useEffect(() => {
    if (listCpt !== undefined && Object.keys(page).length > 0) {
      const listMapped = listCpt.map((cat) => ({
        id: pages[cat].id,
        title: pages[cat].title.rendered,
        text: pages[cat].content.rendered,
        image: getImageUrl(pages[cat].featured_media, 'large', cat, 'pages'),
      }))
      setInsurance(listMapped)
    }
  }, [listCpt, page])

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Activate Page Animation
  ------------------------------*/
  useEffect(() => {
    if (load) setPageAnimationReady(true)
  }, [load])

  /*------------------------------
  If there are products in category complete the preload
  ------------------------------*/
  useEffect(() => {
    if (insurance.length > 0) {
      preloadMedia()
    }
  }, [insurance])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta
        title={page.yoast_meta.find((y) => y.property?.includes('title')).content || page.title.rendered}
        meta={page.yoast_meta}
        schema={page.yoast_json_ld}
      />
    )
  }

  /*------------------------------
  Render Hero
  ------------------------------*/
  const renderHero = () => {
    return page.featured_media && (
      <Hero
        img={getImageUrl(page.featured_media, 'full', stub.current, 'pages')}
        heightPercent={mediaQueryKey > 2 ? '30%' : '56.25%'}
      />
    )
  }

  /*------------------------------
  Render Category Description
  ------------------------------*/
  const renderDescription = () => {
    return (
      <div className={`${classes.wrapper} ${classes.description}`}>
        <CategoryDescription
          title={page.title.rendered}
          info={page.content.rendered}
          reverse
        />
      </div>
    )
  }

  /*------------------------------
  Render Badge
  ------------------------------*/
  const renderBadge = () => {
    return insurance.length > 0 && (
      <div className={`${classes.wrapper} ${classes.insurance}`}>
        <RevealGrid
          cols={3}
        >
          {insurance.map((insu) => (
            <Badge
              key={insu.id}
              item={insu}
            />
          ))}
        </RevealGrid>
      </div>
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => <Footer />

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderHero()}
        {renderDescription()}
        {renderBadge()}
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageArchiveAssicurazioni ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default Assicurazioni
